// src/pages/ShareExperience.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./ShareExperience.css";

/* Where testimonials are emailed. Change if needed. */
const REVIEW_EMAIL = "info@mosikeelectrical.com";

const SERVICES = [
  "Electrical Wiring",
  "Lighting Installation",
  "Circuit Design",
  "Power Backup Systems",
  "Safety Inspection & Compliance",
  "Specialized Electrical Installations",
  "Residential",
  "Commercial",
  "Industrial",
  "Government",
  "Other",
];

export default function ShareExperience() {
  const [form, setForm] = useState({
    name: "",
    location: "",
    service: "",
    rating: 5,
    text: "",
    consent: false,
  });
  const [submitted, setSubmitted] = useState(false);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  // A ready-to-paste JSON object for testimonials.json
  const jsonSnippet = JSON.stringify(
    {
      name: form.name.trim(),
      location: form.location.trim(),
      service: form.service,
      rating: Number(form.rating),
      text: form.text.trim(),
    },
    null,
    2
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    const subject = `New testimonial from ${form.name || "a client"}`;
    const body =
      `A client submitted a testimonial via the website.\n\n` +
      `Name: ${form.name}\n` +
      `Location: ${form.location}\n` +
      `Service: ${form.service}\n` +
      `Rating: ${form.rating} / 5\n\n` +
      `Testimonial:\n"${form.text}"\n\n` +
      `Consent to publish: ${form.consent ? "Yes" : "No"}\n\n` +
      `------------------------------------------------------------\n` +
      `To publish: paste this block into src/data/testimonials.json\n` +
      `------------------------------------------------------------\n` +
      `${jsonSnippet}\n`;

    const mailto = `mailto:${REVIEW_EMAIL}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;

    // Open the client's email app with everything pre-filled
    window.location.href = mailto;
    setSubmitted(true);
  };

  const valid =
    form.name.trim() && form.text.trim() && form.consent;

  if (submitted) {
    return (
      <main className="share-experience">
        <div className="se-card se-thanks">
          <i className="fas fa-check-circle" />
          <h1>Thank you!</h1>
          <p>
            Your email app should have opened with your review ready to send. If
            it didn't, please email it to{" "}
            <a href={`mailto:${REVIEW_EMAIL}`}>{REVIEW_EMAIL}</a>.
          </p>
          <p className="se-muted">
            We review every testimonial before it appears on the site.
          </p>
          <Link to="/" className="btn">
            Back to Home
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="share-experience">
      <div className="se-card">
        <h1 className="se-title">Share Your Experience</h1>
        <p className="se-intro">
          Thank you for working with Mosike Electrical Contractors. Tell us how
          we did — your words help other clients choose us with confidence.
        </p>

        <form className="se-form" onSubmit={handleSubmit}>
          <label>
            Your name <span className="se-req">*</span>
            <input
              type="text"
              value={form.name}
              onChange={update("name")}
              placeholder="e.g. Linda B."
              required
            />
          </label>

          <label>
            Town / Suburb
            <input
              type="text"
              value={form.location}
              onChange={update("location")}
              placeholder="e.g. Pretoria"
            />
          </label>

          <label>
            Which service did we provide?
            <select value={form.service} onChange={update("service")}>
              <option value="">Select a service…</option>
              {SERVICES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>

          <div className="se-rating-field">
            <span>Your rating</span>
            <div className="se-stars" role="radiogroup" aria-label="Star rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  type="button"
                  key={n}
                  className={`se-star ${n <= form.rating ? "active" : ""}`}
                  aria-label={`${n} star${n > 1 ? "s" : ""}`}
                  aria-pressed={n === form.rating}
                  onClick={() => setForm((f) => ({ ...f, rating: n }))}
                >
                  <i className="fas fa-star" />
                </button>
              ))}
            </div>
          </div>

          <label>
            Your testimonial <span className="se-req">*</span>
            <textarea
              rows={5}
              value={form.text}
              onChange={update("text")}
              placeholder="Tell us about the work we did and your experience…"
              required
            />
          </label>

          <label className="se-consent">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) =>
                setForm((f) => ({ ...f, consent: e.target.checked }))
              }
            />
            <span>
              I give Mosike Electrical Contractors permission to publish this
              testimonial on their website.
            </span>
          </label>

          <button type="submit" className="btn se-submit" disabled={!valid}>
            <i className="fas fa-paper-plane" /> Send Testimonial
          </button>
          <p className="se-note">
            Submitting opens your email app with the review ready to send to us.
          </p>
        </form>
      </div>
    </main>
  );
}
